import AuthLayout from "components/auth/AuthLayout";
import AddUpdateUser from "components/views/admin/user/AddUpdateUser";
import Layout from "layout/Layout";
import { lazy } from "react";
import { Route, Routes } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";
import RoleRoute from "./RoleRoute";
import DistrictReport from "components/views/admin/reports/DistrictReport";
import LawOfficerReport from "components/views/admin/reports/LawOfficerReport";
import UnderSectionReport from "components/views/admin/reports/UnderSectionReport";

const Auth = lazy(() => import("../components/auth/AuthLayout"));
const Dashboard = lazy(() => import("../components/views/admin/Dashboard"));
const EReplyMarking = lazy(() =>
  import("../components/views/admin/ereply/EReplyMarking")
);
const MarkedEReply = lazy(() =>
  import("../components/views/admin/ereply/MarkedEReply")
);
const PaymentDetails = lazy(() =>
  import("../components/views/admin/payment/payment/PaymentDetails")
);
const AddPaymentDetails = lazy(() =>
  import("../components/views/admin/payment/payment/AddPaymentDetails")
);
const EReply = lazy(() => import("../components/views/police/ereply/EReply"));
const AddUpdateEReply = lazy(() =>
  import("../components/views/police/ereply/AddUpdateEReply")
);
const UploadDocuments = lazy(() =>
  import("../components/views/police/ereply/UploadDocuments")
);
const RetainershipPayment = lazy(() =>
  import("../components/views/admin/payment/retainership/RetainershipPayment")
);
const AddUpdateRetainerPayment = lazy(() =>
  import(
    "../components/views/admin/payment/retainership/AddUpdateRetainershipPayment"
  )
);
const Users = lazy(() => import("../components/views/admin/user/Users"));
const Documents = lazy(() =>
  import("../components/views/admin/documents/Documents")
);
const Posts = lazy(() => import("../components/views/admin/post/Posts"));
const PasswordReset = lazy(() =>
  import("../components/views/admin/user/PasswordReset")
);
const ReadyToSendCase = lazy(() =>
  import("../components/views/police/ereply/ReadyToSendCase")
);
const EReplyStatus = lazy(() =>
  import("../components/views/police/ereply/EReplyStatus")
);
const ReceivedReplies = lazy(() =>
  import("../components/views/advocate/ReceivedReplies")
);
const AcceptedReplies = lazy(() =>
  import("../components/views/advocate/AcceptedReplies")
);
const ReportCaseList = lazy(() =>
  import("../components/views/police/ReportCaseList")
);
const ViewEReply = lazy(() => import("../components/views/common/ViewEReply"));
const ViewDocuments = lazy(() =>
  import("../components/views/common/ViewDocuments")
);
const RequiredDocumentsReply = lazy(() => import("../components/views/advocate/RequiredDocumentsReply"));
const Maintainance = lazy(() => import("../components/error/Maintainance"));
const UploadDocumentsInReply = lazy(() => import("../components/views/police/ereply/UploadDocumentsInReply"));

const DraftReply = lazy(() =>
  import("../components/views/advocate/DraftReply")
);
const ViewDraftReply = lazy(() =>
  import("../components/views/police/ereply/ViewDraftReply")
);
const PoliceStationListing = lazy(() =>
  import("../components/views/admin/policeStations/PoliceStationList")
);
const PoliceStationWiseReport = lazy(() =>
  import("../components/views/admin/reports/PoliceStationWiseReport")
);
const GenerateReportList = lazy(() =>
  import("../components/views/admin/reports/GenerateReportList")
);
const Error404 = lazy(() => import("../components/error/Error404"));

// ─── Role constants ────────────────────────────────────────────────────────
const ALL     = ["Admin", "Police", "Advocate"];
const ADMIN   = ["Admin"];
const POLICE  = ["Police"];
const ADVOC   = ["Advocate"];
const ADM_POL = ["Admin", "Police"];
const ADM_ADV = ["Admin", "Advocate"];
const POL_ADV = ["Police", "Advocate"];

function DashboardRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        {/* ── No role restriction ── */}
        <Route path="/change-password" element={<Auth />} />
        <Route path="/maintainance" element={<Maintainance />} />

        {/* ── All roles ── */}
        <Route path="/dashboard" element={<RoleRoute roles={ALL}><Dashboard /></RoleRoute>} />
        <Route path="/v-er"     element={<RoleRoute roles={ALL}><ViewEReply /></RoleRoute>} />
        <Route path="/v-docs"   element={<RoleRoute roles={ALL}><ViewDocuments /></RoleRoute>} />
        <Route index            element={<RoleRoute roles={ALL}><Dashboard /></RoleRoute>} />

        {/* ── Admin only ── */}
        <Route path="/erm"     element={<RoleRoute roles={ADMIN}><EReplyMarking /></RoleRoute>} />
        <Route path="/m-er"    element={<RoleRoute roles={ADMIN}><MarkedEReply /></RoleRoute>} />
        <Route path="/users"   element={<RoleRoute roles={ADMIN}><Users /></RoleRoute>} />
        <Route path="/create-user" element={<RoleRoute roles={ADMIN}><AddUpdateUser /></RoleRoute>} />
        <Route path="/pr"      element={<RoleRoute roles={ADMIN}><PasswordReset /></RoleRoute>} />
        <Route path="/documents" element={<RoleRoute roles={ADMIN}><Documents /></RoleRoute>} />
        <Route path="/posts"   element={<RoleRoute roles={ADMIN}><Posts /></RoleRoute>} />
        <Route path="/p-s"     element={<RoleRoute roles={ADMIN}><PoliceStationListing /></RoleRoute>} />
        <Route path="/pay"     element={<RoleRoute roles={ADMIN}><PaymentDetails /></RoleRoute>} />
        <Route path="/au-pd"   element={<RoleRoute roles={ADMIN}><AddPaymentDetails /></RoleRoute>} />
        <Route path="/rp"      element={<RoleRoute roles={ADMIN}><RetainershipPayment /></RoleRoute>} />
        <Route path="/au-rp"   element={<RoleRoute roles={ADMIN}><AddUpdateRetainerPayment /></RoleRoute>} />
        <Route path="/dr"      element={<RoleRoute roles={ADMIN}><DistrictReport /></RoleRoute>} />
        <Route path="/pswr"    element={<RoleRoute roles={ADMIN}><PoliceStationWiseReport /></RoleRoute>} />
        <Route path="/grl"     element={<RoleRoute roles={ADMIN}><GenerateReportList /></RoleRoute>} />

        {/* ── Police only ── */}
        <Route path="/ereply"   element={<RoleRoute roles={POLICE}><EReply /></RoleRoute>} />
        <Route path="/rts"      element={<RoleRoute roles={POLICE}><ReadyToSendCase /></RoleRoute>} />
        <Route path="/au-er"    element={<RoleRoute roles={POLICE}><AddUpdateEReply /></RoleRoute>} />
        <Route path="/au-docs"  element={<RoleRoute roles={POLICE}><UploadDocuments /></RoleRoute>} />
        <Route path="/ers"      element={<RoleRoute roles={POLICE}><EReplyStatus /></RoleRoute>} />
        <Route path="/r-ca"     element={<RoleRoute roles={POLICE}><ReportCaseList /></RoleRoute>} />
        <Route path="/udr"      element={<RoleRoute roles={POLICE}><UploadDocumentsInReply /></RoleRoute>} />

        {/* ── Advocate only ── */}
        <Route path="/r-er"    element={<RoleRoute roles={ADVOC}><ReceivedReplies /></RoleRoute>} />
        <Route path="/a-er"    element={<RoleRoute roles={ADVOC}><AcceptedReplies /></RoleRoute>} />
        <Route path="/rd-rp"   element={<RoleRoute roles={ADVOC}><RequiredDocumentsReply /></RoleRoute>} />
        <Route path="/mdr"     element={<RoleRoute roles={ADVOC}><DraftReply /></RoleRoute>} />

        {/* ── Admin + Police ── */}
        <Route path="/usr" element={<RoleRoute roles={ADM_POL}><UnderSectionReport /></RoleRoute>} />

        {/* ── Admin + Advocate ── */}
        <Route path="/lor" element={<RoleRoute roles={ADM_ADV}><LawOfficerReport /></RoleRoute>} />

        {/* ── Police + Advocate ── */}
        <Route path="/v-dr" element={<RoleRoute roles={POL_ADV}><ViewDraftReply /></RoleRoute>} />
      </Route>
      <Route path="*" element={<Error404 />} />
    </Routes>
  );
}

export default DashboardRoutes;
